import type { Matter } from "@/shared/cache/slices/matters";

/**
 * Scope keys used to resolve restricted words for a matter.
 *
 * `clientCode` is what `billing_guidelines.client_code` stores for the tenant;
 * `matterId` is what `billing_guidelines.matter_id` stores.
 */
export type MatterScope = {
	clientCode: string;
	matterId: string;
};

/**
 * Resolve the client and matter scope keys for a matter.
 *
 * Tenants differ in what a matter id carries. Gould Ratner encodes the client
 * in it as "CLIENT.MATTER", so the client is the prefix. Other tenants use an
 * opaque id and keep the client in its own column — there the client name is
 * the only stable client key, because `client_code` holds the matter's opening
 * year rather than a client identifier.
 *
 * Returns null when no client can be determined, which degrades to firm-wide
 * rules rather than mis-scoping to a key that matches nothing.
 */
export function resolveMatterScope(
	matterId: string | null | undefined,
	clientName: string | null | undefined,
): MatterScope | null {
	if (!matterId) return null;

	const [head = "", ...rest] = matterId.split(".");
	if (rest.length > 0) {
		return { clientCode: head.trim(), matterId: rest.join(".").trim() };
	}

	const client = clientName?.trim();
	if (!client) return null;
	return { clientCode: client, matterId: matterId.trim() };
}

/** Scope keys for a matter record. Falls back to the raw id when unresolvable. */
export function matterScopeParts(matter: Matter): MatterScope {
	return (
		resolveMatterScope(matter.matter_id, matter.client) ?? {
			clientCode: "",
			matterId: matter.matter_id.trim(),
		}
	);
}

/** Scope keys for a matter id, using the matters cache to find its client. */
export function matterScopeById(
	matterId: string | null | undefined,
	matters: Matter[],
): MatterScope | null {
	if (!matterId) return null;
	const matter = matters.find((candidate) => candidate.matter_id === matterId);
	return resolveMatterScope(matterId, matter?.client);
}
